import { createSlice } from '@reduxjs/toolkit';

/** Token strength is 0–100 from four criteria (25% each), aligned with DexScreener:
 * 1. Token created (exists on-chain)
 * 2. Has liquidity (DexScreener token-pairs)
 * 3. Mint authority revoked (mintAuthority === null)
 * 4. Freeze authority revoked (freezeAuthority === null)
 */
const POINTS_PER_CRITERIA = 25;

const initialState = {
  /** Prefill data when coming from token creator (mintAddress, symbol, etc.) */
  tokenData: null,
  /** Fetched criteria for the current mint; score is derived from these */
  strengthCriteria: null,
  strengthLoading: false,
  strengthError: null,
};

const tokenStrengthSlice = createSlice({
  name: 'tokenStrength',
  initialState,
  reducers: {
    setStrengthCriteria: (state, action) => {
      state.strengthCriteria = action.payload;
      state.strengthError = null;
    },
    setStrengthLoading: (state, action) => {
      state.strengthLoading = action.payload;
      if (action.payload) state.strengthError = null;
    },
    setStrengthError: (state, action) => {
      state.strengthError = action.payload;
    },
    /** Optimistic update: set one criterion (e.g. after user creates pool / revokes on our app) */
    updateStrengthCriterion: (state, action) => {
      const { key, value } = action.payload;
      if (!state.strengthCriteria) return;
      if (
        key === 'tokenExists' ||
        key === 'hasLiquidity' ||
        key === 'mintAuthorityRevoked' ||
        key === 'freezeAuthorityRevoked' ||
        key === 'liquidityLocked'
      ) {
        state.strengthCriteria[key] = !!value;
      }
    },
    markTokenCreated: (state, action) => {
      state.tokenData = action.payload;
      if (state.strengthCriteria && action.payload?.mintAddress === state.strengthCriteria.mintAddress) {
        state.strengthCriteria.tokenExists = true;
      }
    },
    markLiquidityPoolCreated: (state) => {
      if (state.strengthCriteria) {
        state.strengthCriteria.hasLiquidity = true;
      }
    },
    resetTokenStrength: (state) => {
      state.tokenData = null;
      state.strengthCriteria = null;
      state.strengthLoading = false;
      state.strengthError = null;
    },
  },
});

export const {
  setStrengthCriteria,
  setStrengthLoading,
  setStrengthError,
  updateStrengthCriterion,
  markTokenCreated,
  markLiquidityPoolCreated,
  resetTokenStrength,
} = tokenStrengthSlice.actions;

/** Score 0–100 from the four criteria (25 each) */
export const selectTokenStrengthScore = (state) => {
  const c = state.tokenStrength.strengthCriteria;
  if (!c) return 0;
  let score = 0;
  if (c.tokenExists) score += POINTS_PER_CRITERIA;
  if (c.hasLiquidity) score += POINTS_PER_CRITERIA;
  if (c.mintAuthorityRevoked) score += POINTS_PER_CRITERIA;
  if (c.freezeAuthorityRevoked) score += POINTS_PER_CRITERIA;
  return score;
};

export const selectStrengthCriteria = (state) => state.tokenStrength.strengthCriteria;
export const selectStrengthLoading = (state) => state.tokenStrength.strengthLoading;
export const selectStrengthError = (state) => state.tokenStrength.strengthError;
export const selectTokenData = (state) => state.tokenStrength.tokenData;

export default tokenStrengthSlice.reducer;
